import { Box, Stack, Typography, useTheme } from '@mui/material';
import type { ReactNode } from 'react';

/**
 * Two-column read-only key/value row used inside drilldown panes (audit
 * trace timeline, webhook delivery inspector, security policy detail).
 * Mono font on values that are identifiers so they're greppable.
 */
export function FieldRow({
  label,
  value,
  mono = false,
}: {
  label: ReactNode;
  value: ReactNode;
  mono?: boolean;
}) {
  const theme = useTheme();
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.25, sm: 2 }}
      sx={{
        py: 1,
        borderBottom: `1px solid ${theme.palette.border.subtle}`,
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box sx={{ minWidth: 160, flexShrink: 0 }}>
        <Typography variant="caption" color="text.tertiary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontFamily: mono
              ? '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace'
              : 'inherit',
            fontSize: mono ? 12 : 13,
            wordBreak: 'break-word',
          }}
        >
          {value ?? <Typography variant="caption" color="text.tertiary">—</Typography>}
        </Typography>
      </Box>
    </Stack>
  );
}
